import { useCallback, useState } from 'react';
import {
  FileTextOutlined,
  UploadOutlined,
  GoogleOutlined,
} from '@ant-design/icons';
import { FileSource } from '@server/application/files/types/file.types';
import { Button, Modal, UploadFile } from 'antd';
import { RcFile } from 'antd/es/upload';
import { LocalFileUpload } from '@/entities/file';
import { useDataInvalidation } from '@/shared/hooks';
import { ConnectBtn } from '@/shared/ui';
import { ConfluenceFileExplorer } from './ConfluenceFileExplorer';
import { GDriveFileExplorer } from './GDriveFileExplorer';
import { useConfluenceConnection } from './useConfluenceConnection';
import { useGDriveConnection } from './useGDriveConnection';
import { useLocalFileUpload } from './useLocalFileUpload';

export const KnowledgeBaseUploadSources = () => {
  const invalidate = useDataInvalidation('files');
  const [currentSource, setCurrentSource] = useState<FileSource | null>(null);
  const [fileUploading, onFileUpload] = useLocalFileUpload();

  const {
    onGDriveConnect,
    onGDriveDisconnect,
    gDriveConnected,
    gDriveIsLoading,
  } = useGDriveConnection();
  const {
    onConfluenceConnect,
    onConfluenceDisconnect,
    confluenceConnected,
    confluenceIsLoading,
  } = useConfluenceConnection();

  const handleFilesAttach = useCallback(() => {
    invalidate();
    setCurrentSource(null);
  }, [invalidate]);

  const handleFileUpload = useCallback(
    async (file: UploadFile, source: FileSource) => {
      await onFileUpload(file as RcFile, source);
      handleFilesAttach();
    },
    [handleFilesAttach, onFileUpload],
  );

  return (
    <div style={{ marginBottom: 20 }}>
      <Button
        icon={<UploadOutlined />}
        onClick={() => setCurrentSource(FileSource.LOCAL)}
        style={{ marginRight: 10 }}
      >
        Local Upload
      </Button>
      <ConnectBtn
        conected={gDriveConnected}
        connectLabel="Connect Google Drive"
        disconnectLabel="Disconnect Google Drive"
        defaultLabel="Google Drive"
        icon={<GoogleOutlined />}
        loading={gDriveIsLoading}
        onDefaultClick={() => setCurrentSource(FileSource.GOOGLE_DRIVE)}
        onConnect={onGDriveConnect}
        onDisconnect={() => {
          Modal.confirm({
            title: 'Disconnect Google Drive',
            content: 'Are you sure you want to disconnect Google Drive?',
            okText: 'Disconnect',
            okButtonProps: { danger: true },
            onOk: onGDriveDisconnect,
          });
        }}
      />
      <ConnectBtn
        conected={confluenceConnected}
        connectLabel="Connect Confluence"
        disconnectLabel="Disconnect Confluence"
        defaultLabel="Confluence"
        icon={<FileTextOutlined />}
        loading={confluenceIsLoading}
        onDefaultClick={() => setCurrentSource(FileSource.CONFLUENCE)}
        onConnect={onConfluenceConnect}
        onDisconnect={() => {
          Modal.confirm({
            title: 'Disconnect Confluence',
            content: 'Are you sure you want to disconnect Confluence?',
            okText: 'Disconnect',
            okButtonProps: { danger: true },
            onOk: onConfluenceDisconnect,
          });
        }}
      />
      <Modal
        open={!!currentSource}
        onCancel={() => setCurrentSource(null)}
        footer={null}
        title="Upload Files"
        width={800}
      >
        {currentSource === FileSource.LOCAL && (
          <LocalFileUpload
            fileUploading={fileUploading}
            onUpload={handleFileUpload}
          />
        )}
        {currentSource === FileSource.GOOGLE_DRIVE && (
          <GDriveFileExplorer
            onFilesAttach={handleFilesAttach}
            isConnected={gDriveConnected}
          />
        )}
        {currentSource === FileSource.CONFLUENCE && (
          <ConfluenceFileExplorer
            onFilesAttach={handleFilesAttach}
            isConnected={confluenceConnected}
          />
        )}
      </Modal>
    </div>
  );
};
